// Miscellaneous utility functions
export const decodeHtmlEntities = (str) => {
    try {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = str;
        return textarea.value;
    } catch (e) {
        return str;
    }
};

export const removeCDATA = (str) => {
    return str.replace(/^<!\[CDATA\[|\]\]>$/g, '');
};

// Prettify JSON strings
export const formatJsonString = (str) => {
    try {
        const jsonObject = JSON.parse(str);
        return JSON.stringify(jsonObject, null, 2); // Prettify JSON with indentation
    } catch (e) {
        return str; // Return the original string if it's not JSON
    }
};

// Format XML with indentation
export const formatXmlString = (xml) => {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xml, 'application/xml');
        const xsltDoc = new DOMParser().parseFromString([
            // XSLT to add line breaks and indentations
            '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" version="1.0">',
            '  <xsl:output method="xml" indent="yes"/>',
            '  <xsl:template match="/">',
            '    <xsl:copy-of select="."/>',
            '  </xsl:template>',
            '</xsl:stylesheet>',
        ].join('\n'), 'application/xml');

        const xsltProcessor = new XSLTProcessor();
        xsltProcessor.importStylesheet(xsltDoc);

        const resultDoc = xsltProcessor.transformToDocument(xmlDoc);
        const serializer = new XMLSerializer();
        return serializer.serializeToString(resultDoc);
    } catch (e) {
        return xml; // Return original string if not valid XML
    }
};

// Detect data format (JSON, XML, or plain text) and format accordingly
export const formatIncomingData = (data) => {
    if (!data) return null;

    const trimmedData = data.trim();

    // Check if it's JSON
    if (trimmedData.startsWith('{') || trimmedData.startsWith('[')) {
        return formatJsonString(trimmedData);
    }

    // Check if it's XML
    if (trimmedData.startsWith('<') && trimmedData.endsWith('>')) {
        return formatXmlString(trimmedData);
    }

    // Fallback for plain text
    return trimmedData;
};

// Main function to parse and format incoming data
export const parseIncomingData = (incomingData) => {
    if (incomingData) {
        console.log("Incoming Data Before Cleanup:", incomingData);

        const cleanData = incomingData
            .replace(/,\s*([}\]])/g, '$1') // Handle trailing commas before } or ]
            .replace(/,\s*$/g, '')          // Handle trailing commas at the end of the string
            .replace(/'$/g, '')           // Handle trailing single quote
            .replace("']]$", "")
            .replace(/\]\]$/, "")
            .slice(0, -1);                // Remove last character

        console.log("Cleaned Data After Cleanup:", cleanData);

        // Process further: decode HTML, remove CDATA, format JSON/XML
        let decodedData = decodeHtmlEntities(cleanData.replace(/\]\]$/, ""));
        decodedData = removeCDATA(decodedData);
        decodedData = formatIncomingData(decodedData);

        return decodedData;
    }
    return null;
};


// Map boomiStatus to step
export const getStepFromStatus = (status) => {
    if (/^Sent.*/.test(status)) {
        return 'Sent';
    }

    switch (status) {
        case 'Received':
        case 'Received-Error':
            return 'Received';
        case 'Translated':
        case 'Translated-Error':
            return 'Processing';
        default:
            return '';
    }
};


export const getStatusText = (status) => {
    if (/^Sent.*/.test(status)) {
        return 'sent';
    }
    switch (status) {
        case 'Received':
        case 'Received-Error':
            return 'received';
        case 'Translated':
        case 'Translated-Error':
            return 'processing';
        default:
            return '';
    }
};

export const parseDate = (input) => {
    if (!input) return ''; // Handle invalid input

    try {
        const datePart = input.substring(0, 8);
        const timePart = input.substring(9, 15);
        const millisecondsPart = input.substring(16);

        const year = datePart.substring(0, 4);
        const month = datePart.substring(4, 6);
        const day = datePart.substring(6, 8);

        const hours = timePart.substring(0, 2);
        const minutes = timePart.substring(2, 4);
        const seconds = timePart.substring(4, 6);

        const date = new Date(year, month - 1, day, hours, minutes, seconds, millisecondsPart);

        const options = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            fractionalSecondDigits: 3
        };

        return date.toLocaleDateString('en-US', options);
    } catch (e) {
        return input; // Return the original input if parsing fails
    }
};

export const convertToYesNo = (value) => {
    return value === 1 ? 'YES' : 'NO';
};

export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};
// validate multiple email addresses
export const areValidEmails = (emailString) => {
    // Split by commas or semicolons and trim each email
    const emails = emailString.split(/[;,]/).map(email => email.trim());
    return emails.every(email => isValidEmail(email));
};

export const renderDropdownContent = (systemType, activeOutboundSubTab, activeSubSubTab, handleSubSubTabClick) => {
    if (activeOutboundSubTab === systemType) {
        return (
            <div className="dropdown-content">
                <button
                    className={`child-tab-button ${activeSubSubTab === "transactionStatus" ? "active" : ""}`}
                    onClick={() => handleSubSubTabClick("transactionStatus")}
                >
                    Transaction Tracking
                </button>
                <button
                    className={`child-tab-button ${activeSubSubTab === "transactionHistory" ? "active" : ""}`}
                    onClick={() => handleSubSubTabClick("transactionHistory")}
                >
                    Transaction History
                </button>
            </div>
        );
    }
    return null;
};

export const getCssClassName = (step, transactions) => {
    if (!transactions || transactions.length === 0) {
        return 'inactive';
    }

    const activeStatuses = transactions.map(transaction => getStepFromStatus(transaction?.boomiStatus));
    const hasError = transactions.some(transaction =>
        /Received-Error|Translated-Error|Sent-Error|Sent-C2C-Error|Sent-TMF-Error|Sent-Footprint-Error|Sent-TP-Error|Sent-Softeon-Error/.test(transaction.boomiStatus)
        && getStepFromStatus(transaction.boomiStatus) === step
    );

    if (activeStatuses.includes(step)) {
        if (hasError) {
            return 'completed error';
        }
        return activeStatuses.indexOf(step) < activeStatuses.length ? 'completed' : 'active';
    }

    return 'inactive';
};

export const getIconColor = (step, transactions) => {
    if (!transactions || transactions.length === 0) return '';
    const activeStatuses = transactions.map(transaction => getStepFromStatus(transaction?.boomiStatus));
    const transaction = transactions[activeStatuses.indexOf(step)];
    const statusStep = getStepFromStatus(transaction?.boomiStatus);

    if (step === statusStep) {
        if (/Received-Error|Translated-Error|Sent-Error|Sent-C2C-Error|Sent-TMF-Error|Sent-Footprint-Error|Sent-TP-Error|Sent-Softeon-Error/.test(transaction?.boomiStatus)) {
            return 'error';
        } else if (statusStep === 'Received') {
            return 'received';
        } else if (statusStep === 'Processing') {
            return 'processing';
        } else if (statusStep === 'Sent') {
            return 'sent';
        }
    }
    return '';
};


export const syntaxHighlight = (json) => {
    if (typeof json !== 'string') {
        json = JSON.stringify(json, null, 2);
    }
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g, function (match) {
        let cls = 'json-number';
        if (/^"/.test(match)) {
            if (/:$/.test(match)) {
                cls = 'json-key';
            } else {
                cls = 'json-string';
            }
        } else if (/true|false/.test(match)) {
            cls = 'json-boolean';
        } else if (/null/.test(match)) {
            cls = 'json-null';
        }
        return `<span class="${cls}">${match}</span>`;
    });
};


export const getFlowLinkType = (transaction) => {
    switch (transaction.messageFlowType) {
        case 'OUTBOUND':
            return 'Inbound';
        case 'INBOUND':
            return 'Outbound';
        default:
            return 'Unknown';
    }
};

